// Minimal ABI fragments for the v1 SDK encoders + reads.
// Source of truth: cli/src/lib/abis.ts. Keep in lockstep.
export const SYNDICATE_VAULT_ABI = [
    // ERC-4626
    {
        name: "deposit",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [
            { name: "assets", type: "uint256" },
            { name: "receiver", type: "address" },
        ],
        outputs: [{ name: "shares", type: "uint256" }],
    },
    {
        name: "redeem",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [
            { name: "shares", type: "uint256" },
            { name: "receiver", type: "address" },
            { name: "owner", type: "address" },
        ],
        outputs: [{ name: "", type: "uint256" }],
    },
    {
        name: "requestRedeem",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [
            { name: "shares", type: "uint256" },
            { name: "owner", type: "address" },
        ],
        outputs: [{ name: "requestId", type: "uint256" }],
    },
    // Reads
    {
        name: "asset",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "address" }],
    },
    {
        name: "totalAssets",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "uint256" }],
    },
    {
        name: "totalSupply",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "uint256" }],
    },
    {
        name: "decimals",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "uint8" }],
    },
    {
        name: "balanceOf",
        type: "function",
        stateMutability: "view",
        inputs: [{ name: "account", type: "address" }],
        outputs: [{ name: "", type: "uint256" }],
    },
    {
        name: "owner",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "address" }],
    },
    {
        name: "governor",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "address" }],
    },
    {
        name: "redemptionsLocked",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "bool" }],
    },
    {
        name: "paused",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "bool" }],
    },
    {
        name: "openDeposits",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "bool" }],
    },
    {
        name: "agentFeeBps",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "uint256" }],
    },
    {
        name: "setAgentFeeBps",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [{ name: "bps", type: "uint256" }],
        outputs: [],
    },
    {
        name: "AgentFeeUpdated",
        type: "event",
        inputs: [{ name: "bps", type: "uint256", indexed: false }],
    },
    {
        name: "getAgentCount",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "uint256" }],
    },
    {
        name: "isApprovedDepositor",
        type: "function",
        stateMutability: "view",
        inputs: [{ name: "depositor", type: "address" }],
        outputs: [{ name: "", type: "bool" }],
    },
    // Owner-side write surface
    {
        name: "approveDepositor",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [{ name: "depositor", type: "address" }],
        outputs: [],
    },
    {
        name: "removeDepositor",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [{ name: "depositor", type: "address" }],
        outputs: [],
    },
    {
        name: "registerAgent",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [
            { name: "agentAddress", type: "address" },
            { name: "agentId", type: "uint256" },
        ],
        outputs: [],
    },
];
export const SYNDICATE_GOVERNOR_ABI = [
    // Propose (multi-arg). Order MUST match contract: vault, strategy,
    // metadataURI, strategyDuration, executeCalls, settlementCalls, coProposers.
    // Agent fee is no longer a propose() arg — it's the vault's `agentFeeBps`
    // (owner-set), snapshotted onto the proposal at propose time and clamped to
    // maxPerformanceFeeBps at settlement.
    {
        name: "propose",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [
            { name: "vault", type: "address" },
            { name: "strategy", type: "address" },
            { name: "metadataURI", type: "string" },
            { name: "strategyDuration", type: "uint256" },
            {
                name: "executeCalls",
                type: "tuple[]",
                components: [
                    { name: "target", type: "address" },
                    { name: "data", type: "bytes" },
                    { name: "value", type: "uint256" },
                ],
            },
            {
                name: "settlementCalls",
                type: "tuple[]",
                components: [
                    { name: "target", type: "address" },
                    { name: "data", type: "bytes" },
                    { name: "value", type: "uint256" },
                ],
            },
            {
                name: "coProposers",
                type: "tuple[]",
                components: [
                    { name: "agent", type: "address" },
                    { name: "splitBps", type: "uint256" },
                ],
            },
        ],
        outputs: [{ name: "proposalId", type: "uint256" }],
    },
    // Lifecycle writes
    {
        name: "vote",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [
            { name: "proposalId", type: "uint256" },
            { name: "support", type: "uint8" },
        ],
        outputs: [],
    },
    {
        name: "executeProposal",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [{ name: "proposalId", type: "uint256" }],
        outputs: [],
    },
    {
        name: "settleProposal",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [{ name: "proposalId", type: "uint256" }],
        outputs: [],
    },
    {
        name: "cancelProposal",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [{ name: "proposalId", type: "uint256" }],
        outputs: [],
    },
    {
        name: "vetoProposal",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [{ name: "proposalId", type: "uint256" }],
        outputs: [],
    },
    // Emergency settlement (GovernorEmergency, V1.5). `unstick` is the
    // owner-instant pre-committed-calls path; `emergencySettleWithCalls` commits
    // a new calldata hash and opens a guardian-reviewed window. The removed
    // `emergencySettle` is intentionally absent — it is not on the deployed impl.
    {
        name: "unstick",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [{ name: "proposalId", type: "uint256" }],
        outputs: [],
    },
    {
        name: "emergencySettleWithCalls",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [
            { name: "proposalId", type: "uint256" },
            {
                name: "calls",
                type: "tuple[]",
                components: [
                    { name: "target", type: "address" },
                    { name: "data", type: "bytes" },
                    { name: "value", type: "uint256" },
                ],
            },
        ],
        outputs: [],
    },
    // Parameter setters (GovernorParameters, V1.5 — owner-instant, no timelock).
    { name: "setVotingPeriod", type: "function", stateMutability: "nonpayable", inputs: [{ name: "newValue", type: "uint256" }], outputs: [] },
    { name: "setExecutionWindow", type: "function", stateMutability: "nonpayable", inputs: [{ name: "newValue", type: "uint256" }], outputs: [] },
    { name: "setVetoThresholdBps", type: "function", stateMutability: "nonpayable", inputs: [{ name: "newValue", type: "uint256" }], outputs: [] },
    { name: "setMaxPerformanceFeeBps", type: "function", stateMutability: "nonpayable", inputs: [{ name: "newValue", type: "uint256" }], outputs: [] },
    { name: "setMaxStrategyDuration", type: "function", stateMutability: "nonpayable", inputs: [{ name: "newValue", type: "uint256" }], outputs: [] },
    { name: "setCooldownPeriod", type: "function", stateMutability: "nonpayable", inputs: [{ name: "newValue", type: "uint256" }], outputs: [] },
    // Reads
    {
        name: "getProposal",
        type: "function",
        stateMutability: "view",
        inputs: [{ name: "proposalId", type: "uint256" }],
        outputs: [
            {
                name: "",
                type: "tuple",
                components: [
                    { name: "id", type: "uint256" },
                    { name: "proposer", type: "address" },
                    { name: "vault", type: "address" },
                    { name: "strategy", type: "address" },
                    { name: "metadataURI", type: "string" },
                    { name: "performanceFeeBps", type: "uint256" },
                    { name: "strategyDuration", type: "uint256" },
                    { name: "votesFor", type: "uint256" },
                    { name: "votesAgainst", type: "uint256" },
                    { name: "votesAbstain", type: "uint256" },
                    { name: "snapshotTimestamp", type: "uint256" },
                    { name: "voteEnd", type: "uint256" },
                    { name: "reviewEnd", type: "uint256" },
                    { name: "executeBy", type: "uint256" },
                    { name: "executedAt", type: "uint256" },
                    { name: "state", type: "uint8" },
                    { name: "vetoThresholdBps", type: "uint256" },
                    { name: "selfManagesFees", type: "bool" },
                ],
            },
        ],
    },
    {
        name: "getProposalState",
        type: "function",
        stateMutability: "view",
        inputs: [{ name: "proposalId", type: "uint256" }],
        outputs: [{ name: "", type: "uint8" }],
    },
    {
        name: "getActiveProposal",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "uint256" }],
    },
    {
        name: "proposalCount",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "uint256" }],
    },
    {
        name: "getGovernorParams",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [
            {
                name: "",
                type: "tuple",
                components: [
                    { name: "votingPeriod", type: "uint256" },
                    { name: "executionWindow", type: "uint256" },
                    { name: "vetoThresholdBps", type: "uint256" },
                    { name: "maxPerformanceFeeBps", type: "uint256" },
                    { name: "cooldownPeriod", type: "uint256" },
                    { name: "collaborationWindow", type: "uint256" },
                    { name: "maxCoProposers", type: "uint256" },
                    { name: "minStrategyDuration", type: "uint256" },
                    { name: "maxStrategyDuration", type: "uint256" },
                ],
            },
        ],
    },
    {
        name: "protocolFeeRecipient",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "address" }],
    },
    {
        name: "FeeClamped",
        type: "event",
        inputs: [
            { name: "proposalId", type: "uint256", indexed: true },
            { name: "snapshotted", type: "uint256", indexed: true },
            { name: "clamped", type: "uint256", indexed: true },
        ],
    },
];
export const SYNDICATE_FACTORY_ABI = [
    {
        name: "createSyndicate",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [
            { name: "creatorAgentId", type: "uint256" },
            {
                name: "config",
                type: "tuple",
                components: [
                    { name: "metadataURI", type: "string" },
                    { name: "asset", type: "address" },
                    { name: "name", type: "string" },
                    { name: "symbol", type: "string" },
                    { name: "openDeposits", type: "bool" },
                    { name: "subdomain", type: "string" },
                ],
            },
        ],
        outputs: [
            { name: "syndicateId", type: "uint256" },
            { name: "vault", type: "address" },
        ],
    },
    {
        name: "syndicateCount",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "uint256" }],
    },
    {
        name: "governorOf",
        type: "function",
        stateMutability: "view",
        inputs: [{ name: "vault", type: "address" }],
        outputs: [{ name: "", type: "address" }],
    },
    {
        name: "protocolConfig",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "address" }],
    },
    {
        name: "subdomainToSyndicate",
        type: "function",
        stateMutability: "view",
        inputs: [{ name: "subdomain", type: "string" }],
        outputs: [{ name: "", type: "uint256" }],
    },
    {
        name: "vaultToSyndicate",
        type: "function",
        stateMutability: "view",
        inputs: [{ name: "vault", type: "address" }],
        outputs: [{ name: "", type: "uint256" }],
    },
    {
        name: "syndicates",
        type: "function",
        stateMutability: "view",
        inputs: [{ name: "id", type: "uint256" }],
        outputs: [
            { name: "id", type: "uint256" },
            { name: "vault", type: "address" },
            { name: "creator", type: "address" },
            { name: "metadataURI", type: "string" },
            { name: "createdAt", type: "uint256" },
            { name: "active", type: "bool" },
            { name: "subdomain", type: "string" },
        ],
    },
    {
        name: "getAllActiveSyndicates",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [
            {
                name: "",
                type: "tuple[]",
                components: [
                    { name: "id", type: "uint256" },
                    { name: "vault", type: "address" },
                    { name: "creator", type: "address" },
                    { name: "metadataURI", type: "string" },
                    { name: "createdAt", type: "uint256" },
                    { name: "active", type: "bool" },
                    { name: "subdomain", type: "string" },
                ],
            },
        ],
    },
];
export const PROTOCOL_CONFIG_ABI = [
    {
        name: "protocolFeeBps",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "uint256" }],
    },
    {
        name: "guardianFeeBps",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "uint256" }],
    },
    {
        name: "protocolFeeRecipient",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "address" }],
    },
    // Protocol-owner setter — protocolFeeBps lives here, not on the governor.
    {
        name: "setProtocolFeeBps",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [{ name: "newValue", type: "uint256" }],
        outputs: [],
    },
];
export const GUARDIAN_REGISTRY_ABI = [
    // Guardian stake
    {
        name: "stakeAsGuardian",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [
            { name: "amount", type: "uint256" },
            { name: "agentId", type: "uint256" },
        ],
        outputs: [],
    },
    {
        name: "requestUnstakeGuardian",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [],
        outputs: [],
    },
    {
        name: "cancelUnstakeGuardian",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [],
        outputs: [],
    },
    {
        name: "claimUnstakeGuardian",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [],
        outputs: [],
    },
    // Delegation
    {
        name: "delegateStake",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [
            { name: "delegate", type: "address" },
            { name: "amount", type: "uint256" },
        ],
        outputs: [],
    },
    {
        name: "requestUnstakeDelegation",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [{ name: "delegate", type: "address" }],
        outputs: [],
    },
    {
        name: "cancelUnstakeDelegation",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [{ name: "delegate", type: "address" }],
        outputs: [],
    },
    {
        name: "claimUnstakeDelegation",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [{ name: "delegate", type: "address" }],
        outputs: [],
    },
    // Commission
    {
        name: "setCommission",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [{ name: "bps", type: "uint16" }],
        outputs: [],
    },
    // Reads
    {
        name: "guardianStake",
        type: "function",
        stateMutability: "view",
        inputs: [{ name: "guardian", type: "address" }],
        outputs: [{ name: "", type: "uint256" }],
    },
    {
        name: "isActiveGuardian",
        type: "function",
        stateMutability: "view",
        inputs: [{ name: "guardian", type: "address" }],
        outputs: [{ name: "", type: "bool" }],
    },
];
export const ERC20_ABI = [
    {
        name: "approve",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [
            { name: "spender", type: "address" },
            { name: "amount", type: "uint256" },
        ],
        outputs: [{ name: "", type: "bool" }],
    },
    {
        name: "allowance",
        type: "function",
        stateMutability: "view",
        inputs: [
            { name: "owner", type: "address" },
            { name: "spender", type: "address" },
        ],
        outputs: [{ name: "", type: "uint256" }],
    },
    {
        name: "balanceOf",
        type: "function",
        stateMutability: "view",
        inputs: [{ name: "account", type: "address" }],
        outputs: [{ name: "", type: "uint256" }],
    },
    {
        name: "decimals",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "uint8" }],
    },
    {
        name: "symbol",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "string" }],
    },
];
// ── ERC-8004 Identity Registry (Agent0) ──
//
// Only the `register(string agentURI, MetadataEntry[] metadata)` overload — the
// one Agent0's `registerOnChain()` actually calls for on-chain data-URI mints.
// agentURI is a base64 `data:application/json` URI; metadata is `[]` for a basic
// agent. Selector 0x8ea42286.
export const IDENTITY_REGISTRY_ABI = [
    {
        name: "register",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [
            { name: "agentURI", type: "string" },
            {
                name: "metadata",
                type: "tuple[]",
                components: [
                    { name: "metadataKey", type: "string" },
                    { name: "metadataValue", type: "bytes" },
                ],
            },
        ],
        outputs: [{ name: "agentId", type: "uint256" }],
    },
];
// ── EAS (Ethereum Attestation Service) — `attest` only ──
//
// Used for SYNDICATE_JOIN_REQUEST and AGENT_APPROVED coordination attestations,
// which always live on Base. Selector 0xf17325e7.
export const EAS_ABI = [
    {
        name: "attest",
        type: "function",
        stateMutability: "payable",
        inputs: [
            {
                name: "request",
                type: "tuple",
                components: [
                    { name: "schema", type: "bytes32" },
                    {
                        name: "data",
                        type: "tuple",
                        components: [
                            { name: "recipient", type: "address" },
                            { name: "expirationTime", type: "uint64" },
                            { name: "revocable", type: "bool" },
                            { name: "refUID", type: "bytes32" },
                            { name: "data", type: "bytes" },
                            { name: "value", type: "uint256" },
                        ],
                    },
                ],
            },
        ],
        outputs: [{ name: "", type: "bytes32" }],
    },
];
// StrategyFactory — atomic clone+initialize wrapper for strategy templates.
// `cloneAndInitDeterministic` deploys the ERC-1167 clone via CREATE2, so the
// clone address is predictable BEFORE the tx is mined (see
// `predictStrategyCloneAddress` in encoders/strategy.ts). This is what unblocks
// keyless / external-signer strategy proposals (issue #387): the proposal's
// executeCalls + the `strategy` arg can be built against the predicted address
// up front, so deploy + propose can both be emitted as unsigned calldata.
export const STRATEGY_FACTORY_ABI = [
    {
        name: "cloneAndInit",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [
            { name: "template", type: "address" },
            { name: "vault", type: "address" },
            { name: "proposer", type: "address" },
            { name: "data", type: "bytes" },
        ],
        outputs: [{ name: "clone", type: "address" }],
    },
    {
        name: "cloneAndInitDeterministic",
        type: "function",
        stateMutability: "nonpayable",
        inputs: [
            { name: "template", type: "address" },
            { name: "vault", type: "address" },
            { name: "proposer", type: "address" },
            { name: "data", type: "bytes" },
            { name: "salt", type: "bytes32" },
        ],
        outputs: [{ name: "clone", type: "address" }],
    },
    {
        name: "syndicateFactory",
        type: "function",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "address" }],
    },
];
